<template>
  <div class="prompts-page">
    <div class="prompts-container">
      <!-- 左侧：场景选择 -->
      <div class="prompts-sidebar">
        <div class="sidebar-content">
          <h3 class="sidebar-title">选择场景</h3>
          <div class="scene-list">
            <div
              v-for="scene in scenes"
              :key="scene"
              class="scene-item"
              :class="{ active: selectedScenes.includes(scene) }"
              @click="toggleScene(scene)"
            >
              <el-checkbox
                :model-value="selectedScenes.includes(scene)"
                @change="toggleScene(scene)"
                @click.stop
              >
                {{ scene }}
              </el-checkbox>
            </div>
            <div v-if="scenes.length === 0" class="no-scenes">
              <span>暂无场景</span>
            </div>
          </div>
          <div class="scene-actions" v-if="selectedScenes.length > 0">
            <el-button size="small" @click="clearSelection">清空选择</el-button>
          </div>
        </div>
      </div>

      <!-- 右侧：提示词列表 -->
      <div class="prompts-content">
        <div class="prompts-list" v-loading="loading">
          <div
            v-for="item in displayItems"
            :key="item.id"
            class="prompt-item"
            @click="showDetailDialog(item)"
          >
            <div class="prompt-item-header">
              <div class="prompt-title-row">
                <h3 class="prompt-item-title">{{ item.title }}</h3>
                <el-tag v-if="item.is_pinned" type="warning" size="small" effect="dark">置顶</el-tag>
              </div>
              <el-tag v-if="item.scene" type="info" size="small">{{ item.scene }}</el-tag>
            </div>
            <div class="prompt-item-content">
              <div class="prompt-item-section">
                <span class="section-label">角色：</span>
                <span class="section-text">{{ truncateText(item.role || '', 60) }}</span>
              </div>
              <div class="prompt-item-section">
                <span class="section-label">提示词：</span>
                <span class="section-text">{{ truncateText(item.prompt || '', 60) }}</span>
              </div>
            </div>
          </div>

          <div v-if="displayItems.length === 0" class="empty-container">
            <el-empty description="暂无提示词" />
          </div>
        </div>

        <el-pagination
          v-if="total > 0"
          :current-page="currentPage"
          :page-sizes="[20, 50, 100, { value: -1, label: '全部' }]"
          :page-size="pageSize"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
          class="pagination"
        />
      </div>
    </div>

    <!-- 详情对话框 -->
    <el-dialog
      v-model="detailDialogVisible"
      :title="selectedItem?.title || '提示词详情'"
      width="800px"
      class="detail-dialog"
    >
      <div v-if="selectedItem" class="detail-content">
        <div class="detail-section">
          <div class="detail-header">
            <span class="detail-label">场景</span>
            <el-tag v-if="selectedItem.scene" type="info">{{ selectedItem.scene }}</el-tag>
            <span v-else class="no-data">未设置</span>
          </div>
        </div>

        <div class="detail-section">
          <div class="detail-header">
            <span class="detail-label">角色</span>
            <el-button
              type="primary"
              size="small"
              @click="copyToClipboard(selectedItem.role, '角色')"
              class="copy-btn"
            >
              <el-icon><DocumentCopy /></el-icon> 复制
            </el-button>
          </div>
          <div class="detail-text">{{ selectedItem.role || '未设置' }}</div>
        </div>

        <div class="detail-section">
          <div class="detail-header">
            <span class="detail-label">提示词</span>
            <el-button
              type="primary"
              size="small"
              @click="copyToClipboard(selectedItem.prompt, '提示词')"
              class="copy-btn"
            >
              <el-icon><DocumentCopy /></el-icon> 复制
            </el-button>
          </div>
          <div class="detail-text">{{ selectedItem.prompt || '未设置' }}</div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import api from '../api'
import { DocumentCopy } from '@element-plus/icons-vue'

export default {
  name: 'PromptsPage',
  components: {
    DocumentCopy
  },
  data() {
    return {
      items: [],
      allItems: [], // 存储所有数据用于提取场景
      scenes: [],
      selectedScenes: [],
      currentPage: 1,
      pageSize: 20,
      total: 0,
      loading: false,
      detailDialogVisible: false,
      selectedItem: null
    }
  },
  computed: {
    displayItems() {
      // loadItems 已经处理了过滤，这里直接返回
      return this.items
    }
  },
  mounted() {
    this.loadAllScenes()
    this.loadItems()
  },
  methods: {
    async loadAllScenes() {
      // 加载所有数据以提取场景列表
      try {
        const response = await api.get('/prompts/templates/', { 
          params: { page_size: 'all' } 
        })
        
        const allData = Array.isArray(response.data) 
          ? response.data 
          : (response.data.results || [])
        
        // 提取所有场景
        const sceneSet = new Set()
        allData.forEach(item => {
          if (item.scene) {
            sceneSet.add(item.scene)
          }
        })
        this.scenes = Array.from(sceneSet).sort()
      } catch (error) {
        console.error('加载场景列表失败:', error)
      }
    },
    async loadItems() {
      this.loading = true
      try {
        // 如果选择了场景，需要加载所有数据以便在前端进行多场景过滤
        // 否则可以使用后端分页
        const needLoadAll = this.selectedScenes.length > 0
        
        const params = {
          page: needLoadAll ? 1 : this.currentPage,
          page_size: needLoadAll || this.pageSize === -1 ? 'all' : this.pageSize
        }
        
        const response = await api.get('/prompts/templates/', { params })
        
        let allItems = []
        if (Array.isArray(response.data)) {
          allItems = response.data
          this.total = response.data.length
        } else {
          allItems = response.data.results || []
          this.total = response.data.count || 0
        }
        
        // 如果选择了场景，在前端进行多场景过滤
        if (this.selectedScenes.length > 0) {
          allItems = allItems.filter(item => 
            item.scene && this.selectedScenes.includes(item.scene)
          )
          // 重新计算总数
          this.total = allItems.length
          
          // 如果使用分页，需要在前端重新分页
          if (this.pageSize !== -1) {
            const start = (this.currentPage - 1) * this.pageSize
            const end = start + this.pageSize
            this.items = allItems.slice(start, end)
          } else {
            this.items = allItems
          }
        } else {
          this.items = allItems
        }
      } catch (error) {
        console.error('加载提示词失败:', error)
        this.items = []
        this.total = 0
        this.$message.error('加载提示词失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },
    toggleScene(scene) {
      const index = this.selectedScenes.indexOf(scene)
      if (index > -1) {
        this.selectedScenes.splice(index, 1)
      } else {
        this.selectedScenes.push(scene)
      }
      // 实时更新
      this.currentPage = 1
      this.loadItems()
    },
    clearSelection() {
      this.selectedScenes = []
      this.currentPage = 1
      this.loadItems()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.loadItems()
    },
    handlePageChange(page) {
      this.currentPage = page
      if (this.pageSize !== -1) {
        this.loadItems()
      }
    },
    truncateText(text, maxLength) {
      if (!text) return ''
      if (text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    },
    showDetailDialog(item) {
      this.selectedItem = item
      this.detailDialogVisible = true
    },
    async copyToClipboard(text, type) {
      if (!text) {
        this.$message.warning(`${type}内容为空`)
        return
      }
      try {
        await navigator.clipboard.writeText(text)
        this.$message.success(`${type}已复制到剪贴板`)
      } catch (error) {
        // 降级方案：使用传统方法
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.style.position = 'fixed'
        textArea.style.opacity = '0'
        document.body.appendChild(textArea)
        textArea.select()
        try {
          document.execCommand('copy')
          this.$message.success(`${type}已复制到剪贴板`)
        } catch (err) {
          this.$message.error('复制失败，请手动复制')
        }
        document.body.removeChild(textArea)
      }
    }
  }
}
</script>

<style scoped>
.prompts-page {
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding: 20px;
}

.prompts-container {
  display: flex;
  gap: 15px;
  min-height: 600px;
}

.prompts-sidebar {
  width: 280px;
  background: rgba(20, 20, 30, 0.8);
  border: 1px solid rgba(0, 150, 255, 0.2);
  border-radius: 8px;
  padding: 20px;
  flex-shrink: 0;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
  overflow-x: hidden;
  /* 自定义滚动条样式 */
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 212, 255, 0.4) rgba(0, 0, 0, 0.2);
}

/* Webkit 浏览器滚动条样式 */
.prompts-sidebar::-webkit-scrollbar {
  width: 6px;
}

.prompts-sidebar::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.prompts-sidebar::-webkit-scrollbar-thumb {
  background: rgba(0, 212, 255, 0.4);
  border-radius: 3px;
  transition: background 0.3s ease;
}

.prompts-sidebar::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 212, 255, 0.6);
}

.sidebar-content {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.sidebar-title {
  color: #e0e0e0;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.scene-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 500px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 5px 0;
  /* 自定义滚动条样式 */
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 212, 255, 0.4) rgba(0, 0, 0, 0.2);
}

/* Webkit 浏览器滚动条样式 */
.scene-list::-webkit-scrollbar {
  width: 6px;
}

.scene-list::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.scene-list::-webkit-scrollbar-thumb {
  background: rgba(0, 212, 255, 0.4);
  border-radius: 3px;
  transition: background 0.3s ease;
}

.scene-list::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 212, 255, 0.6);
}

.scene-item {
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 150, 255, 0.2);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
}

.scene-item:hover {
  background: rgba(0, 150, 255, 0.1);
  border-color: rgba(0, 150, 255, 0.4);
  transform: translateX(3px);
}

.scene-item.active {
  background: rgba(0, 150, 255, 0.2);
  border-color: rgba(0, 212, 255, 0.6);
}

.scene-item :deep(.el-checkbox) {
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.scene-item :deep(.el-checkbox__input) {
  flex-shrink: 0;
}

.scene-item :deep(.el-checkbox__label) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.scene-item :deep(.el-checkbox__label) {
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
}

.scene-item.active :deep(.el-checkbox__label) {
  color: #00d4ff;
  font-weight: 600;
}

.no-scenes {
  padding: 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
}

.scene-actions {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid rgba(0, 150, 255, 0.2);
}

.prompts-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.prompts-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.prompt-item {
  background: rgba(20, 20, 30, 0.8);
  border: 1px solid rgba(0, 150, 255, 0.2);
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.prompt-item:hover {
  border-color: rgba(0, 150, 255, 0.5);
  box-shadow: 0 4px 20px rgba(0, 150, 255, 0.2);
  transform: translateY(-2px);
}

.prompt-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(0, 150, 255, 0.2);
}

.prompt-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.prompt-item-title {
  color: #e0e0e0;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  flex: 1;
}

.prompt-item-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.prompt-item-section {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.section-label {
  color: #00d4ff;
  font-size: 14px;
  font-weight: 600;
}

.section-text {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  line-height: 1.6;
  word-wrap: break-word;
}

.empty-container {
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

/* 详情对话框样式 */
.detail-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.detail-section {
  background: rgba(0, 0, 0, 0.3);
  padding: 15px;
  border-radius: 8px;
  border: 1px solid rgba(0, 150, 255, 0.2);
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.detail-label {
  color: #00d4ff;
  font-weight: 600;
  font-size: 16px;
}

.detail-text {
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.8;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.no-data {
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
}

.copy-btn {
  display: flex;
  align-items: center;
  gap: 5px;
}

/* 响应式布局 */
@media (max-width: 1200px) {
  .prompts-list {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .prompts-container {
    flex-direction: column;
  }

  .prompts-sidebar {
    width: 100%;
  }
}
</style>
